import React, { useEffect, useState } from 'react';
import { ICreatePasswordEntry, IDecryptedPasswordEntry } from '../../types/vault.types';
import Input from '../common/Input';
import Button from '../common/Button';
import { generateStrongPassword } from '../../utils/passwordGenerator';
import { useCategories } from '../../contexts/CategoryContext';
import PasswordStrengthMeter from '../common/PasswordStrengthMeter';
import { toast } from 'react-hot-toast';
import { decryptKeyData } from '../../utils/encryption';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { MultiSelect } from 'react-multi-select-component';
import {
    EyeIcon,
    EyeSlashIcon,
    SparklesIcon,
    GlobeAltIcon,
    UserIcon,
    KeyIcon,
    DocumentTextIcon,
    FolderIcon,
    HeartIcon,
    ShieldCheckIcon,
    PlusIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid';

interface Option {
    label: string;
    value: string;
    disabled?: boolean;
    isShared?: boolean;
    sharedKey?: string;
}

interface PasswordFormProps {
    initialData?: IDecryptedPasswordEntry;
    onSubmit: (data: ICreatePasswordEntry) => Promise<void>;
    onCancel: () => void;
    isLoading?: boolean;
    categoryId?: string;
}

interface FormErrors {
    title?: string;
    username?: string;
    password?: string;
    website_url?: string;
    notes?: string;
    category_id?: string;
}

const calculatePasswordStrength = (password: string, email?: string): string => {
    const feedback: string[] = [];
    let score = 0;

    // Handle null or undefined password
    if (!password) {
        return 'Very Weak';
    }

    // Length check
    if (password.length >= 12) {
        score += 20;
    }

    // Number check
    if (/\d/.test(password)) {
        score += 20;
    }

    // Lowercase check
    if (/[a-z]/.test(password)) {
        score += 20;
    }

    // Uppercase check
    if (/[A-Z]/.test(password)) {
        score += 20;
    }

    // Special character check
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
        score += 20;
    }

    // Email check
    if (email && password.toLowerCase().includes(email.toLowerCase())) {
        score -= 20;
    }

    // Get strength label based on score
    if (score <= 20) return 'Very Weak';
    if (score <= 40) return 'Weak';
    if (score <= 60) return 'Fair';
    if (score <= 80) return 'Strong';
    return 'Very Strong';
};

const PasswordForm: React.FC<PasswordFormProps> = ({
    initialData = null,
    onSubmit,
    onCancel,
    isLoading = false,
    categoryId
}) => {
    const { categories, refreshCategories, createCategory } = useCategories();
    const { activeWorkspace } = useWorkspace();

    // Convert categories to options format for react-multi-select-component
    const categoryOptions: Option[] = categories
        .filter(cat => !(cat.permission_level === 'read'))
        .map(cat => ({
            label: cat.name,
            value: cat.id,
            isShared: cat.is_shared,
            sharedKey: cat.shared_key
        }));

    // Initialize selected options from existing data
    const initializeSelectedOptions = (): Option[] => {
        if (initialData?.categories && initialData.categories.length > 0) {
            return initialData.categories.map(cat => ({
                label: cat.name,
                value: cat.id,
                isShared: cat.is_shared,
                sharedKey: cat.shared_key
            }));
        } else if (categoryId || initialData?.category_id) {
            const singleCategoryId = categoryId || initialData?.category_id;
            const category = categories.find(c => c.id === singleCategoryId);
            if (category) {
                return [{
                    label: category.name,
                    value: category.id,
                    isShared: category.is_shared,
                    sharedKey: category.shared_key
                }];
            }
        }
        return [];
    };

    const [selectedOptions, setSelectedOptions] = useState<Option[]>(initializeSelectedOptions);
    const [isCreatingCategory, setIsCreatingCategory] = useState(false);
    const [newCategoryName, setNewCategoryName] = useState('');
    const [creatingCategoryLoading, setCreatingCategoryLoading] = useState(false);

    const [formData, setFormData] = useState<ICreatePasswordEntry>({
        title: initialData?.title || '',
        username: initialData?.username || '',
        password: initialData?.password || '',
        notes: initialData?.notes || '',
        website_url: initialData?.website_url || '',
        category_id: categoryId || initialData?.category_id || '',
        favourite: initialData?.favourite || false,
        auto_login: initialData?.auto_login || false,
        favicon_url: initialData?.favicon_url || '',
        isSharedUpdate: false,
        sharedKey: undefined,
        password_strength: initialData?.password_strength || ''
    });

    const [errors, setErrors] = useState<FormErrors>({});
    const [showPassword, setShowPassword] = useState(false);
    const [touched, setTouched] = useState<Record<string, boolean>>({});
    const [autoLogin, setAutoLogin] = useState(initialData?.auto_login || false);

    const validateForm = (): boolean => {
        const newErrors: FormErrors = {};

        // Title validation
        if (!formData.title.trim()) {
            newErrors.title = 'Title is required';
        } else if (formData.title.length < 3) {
            newErrors.title = 'Title must be at least 3 characters';
        }

        // Username validation
        if (!formData.username.trim()) {
            newErrors.username = 'Username is required';
        }

        // Password validation
        if (!formData.password) {
            newErrors.password = 'Password is required';
        }

        // Website URL validation (if provided)
        if (formData.website_url) {
            const urlToTest = formData.website_url.trim();

            try {
                const parsedUrl = new URL(urlToTest);
                if (!/^https?:$/.test(parsedUrl.protocol)) {
                    newErrors.website_url = 'URL must start with http:// or https://';
                }
            } catch (_) {
                newErrors.website_url = 'Please enter a valid URL';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle multi-select change
    const handleMultiSelectChange = (selected: Option[]) => {
        setSelectedOptions(selected);

        // Update form data with category information
        const categoryIds = selected.map(option => option.value);

        // Check if any selected category is shared
        const hasSharedCategory = selected.some(opt => opt.isShared);
        const sharedOption = selected.find(opt => opt.isShared);

        setFormData(prev => ({
            ...prev,
            category_ids: categoryIds,
            isSharedUpdate: hasSharedCategory,
            sharedKey: hasSharedCategory && sharedOption?.sharedKey ?
                decryptKeyData(sharedOption.sharedKey) : undefined
        }));
    };

    // Handle category creation
    const handleCreateCategory = async () => {
        if (!newCategoryName.trim()) return;

        setCreatingCategoryLoading(true);
        try {
            const newCategory = await createCategory({
                name: newCategoryName,
                workspace_id: activeWorkspace?.id || undefined
            });
            await refreshCategories();

            // Add the new category to selected options
            const newOption: Option = {
                label: newCategoryName,
                value: newCategory.id,
                isShared: false,
                sharedKey: undefined
            };
            setSelectedOptions([...selectedOptions, newOption]);

            // Reset the create form
            setNewCategoryName('');
            setIsCreatingCategory(false);
            toast.success('Vault created successfully');
        } catch (error) {
            console.error('Error creating vault:', error);
            toast.error('Failed to create vault');
        } finally {
            setCreatingCategoryLoading(false);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target;
        const isCheckbox = type === 'checkbox';
        const newValue = isCheckbox ? (e.target as HTMLInputElement).checked : value;

        // For form fields (not handling category_id since we use multi-select)
        setFormData(prev => {
            if (name === 'favourite' || name === 'auto_login') {
                return {
                    ...prev,
                    [name]: isCheckbox ? Boolean(newValue) : newValue
                };
            } else {
                return {
                    ...prev,
                    [name]: typeof newValue === 'string' ? newValue : prev[name as keyof typeof prev]
                };
            }
        });

        // Clear error when user starts typing
        if (errors[name as keyof FormErrors]) {
            setErrors(prev => ({
                ...prev,
                [name]: undefined
            }));
        }
    };

    const handleBlur = (fieldName: string) => {
        setTouched(prev => ({
            ...prev,
            [fieldName]: true
        }));
        validateForm();
    };

    const handleGeneratePassword = () => {
        const newPassword = generateStrongPassword();
        setFormData(prev => ({ ...prev, password: newPassword }));
        setErrors(prev => ({
            ...prev,
            password: undefined
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const passwordStrength = calculatePasswordStrength(formData.password, formData.username);

        if (validateForm()) {
            await onSubmit({
                ...formData,
                auto_login: autoLogin,
                password_strength: passwordStrength,
            });
        }
    };

    useEffect(() => {
        refreshCategories();

        // Update form data when selected categories change
        if (selectedOptions.length > 0) {
            const categoryIds = selectedOptions.map(option => option.value);
            const hasSharedCategory = selectedOptions.some(opt => opt.isShared);
            const sharedOption = selectedOptions.find(opt => opt.isShared);

            setFormData(prev => ({
                ...prev,
                category_ids: categoryIds,
                isSharedUpdate: hasSharedCategory,
                sharedKey: hasSharedCategory && sharedOption?.sharedKey ?
                    decryptKeyData(sharedOption.sharedKey) : undefined
            }));
        }
    }, [selectedOptions]);

    return (
        <form onSubmit={handleSubmit} className="flex flex-col h-full" autoComplete="off">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Left Column - Basic Information */}
                <div className="lg:col-span-1 space-y-6">
                    {/* Basic Details Card */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <div className="flex items-center space-x-2 mb-4">
                            <div className="p-1 bg-primary-100 rounded-lg">
                                <DocumentTextIcon className="w-4 h-4 text-primary-600" />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900">Basic Details</h3>
                        </div>

                        <div className="space-y-4">
                            {/* Title Field */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Title *
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <GlobeAltIcon className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <input
                                        type="text"
                                        name="title"
                                        value={formData.title}
                                        onChange={handleChange}
                                        onBlur={() => handleBlur('title')}
                                        className={`block w-full pl-10 pr-3 py-3 border rounded-xl text-sm placeholder-gray-400 transition-colors duration-200 ${touched.title && errors.title
                                                ? 'border-red-300 focus:ring-red-500 focus:border-red-500 bg-red-50'
                                                : 'border-gray-300 focus:ring-primary-500 focus:border-primary-500 hover:border-gray-400'
                                            }`}
                                        required
                                        placeholder="e.g., Gmail Account"
                                    />
                                </div>
                                {touched.title && errors.title && (
                                    <p className="mt-2 text-sm text-red-600 flex items-center">
                                        <span className="font-medium">{errors.title}</span>
                                    </p>
                                )}
                            </div>

                            {/* Website URL */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Website URL
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <GlobeAltIcon className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <input
                                        type="text"
                                        name="website_url"
                                        value={formData.website_url}
                                        onChange={handleChange}
                                        onBlur={() => handleBlur('website_url')}
                                        className={`block w-full pl-10 pr-3 py-3 border rounded-xl text-sm placeholder-gray-400 transition-colors duration-200 ${touched.website_url && errors.website_url
                                                ? 'border-red-300 focus:ring-red-500 focus:border-red-500 bg-red-50'
                                                : 'border-gray-300 focus:ring-primary-500 focus:border-primary-500 hover:border-gray-400'
                                            }`}
                                        placeholder="https://example.com"
                                    />
                                </div>
                                {touched.website_url && errors.website_url && (
                                    <p className="mt-2 text-sm text-red-600 flex items-center">
                                        <span className="font-medium">{errors.website_url}</span>
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Vault Selection Card */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <div className="flex items-center space-x-2 mb-4">
                            <div className="p-1 bg-blue-100 rounded-lg">
                                <FolderIcon className="w-4 h-4 text-blue-600" />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900">Vaults</h3>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Select Vaults
                                </label>
                                <div className="space-y-2 sm:space-y-3">
                                    <MultiSelect
                                        options={categoryOptions}
                                        value={selectedOptions}
                                        onChange={handleMultiSelectChange}
                                        labelledBy="Select vaults"
                                        hasSelectAll={false}
                                        className="w-full"
                                        ClearSelectedIcon={null}
                                        debounceDuration={0}
                                    />
                                </div>
                            </div>

                            {/* Create New Vault */}
                            <div className="pt-2 border-t border-gray-200">
                                {!isCreatingCategory ? (
                                    <button
                                        type="button"
                                        onClick={() => setIsCreatingCategory(true)}
                                        className="w-full flex items-center justify-center space-x-2 p-3 text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors duration-200"
                                    >
                                        <PlusIcon className="w-4 h-4" />
                                        <span className="text-sm font-medium">Create New Vault</span>
                                    </button>
                                ) : (
                                    <div className="space-y-3">
                                        <input
                                            type="text"
                                            value={newCategoryName}
                                            onChange={(e) => setNewCategoryName(e.target.value)}
                                            placeholder="Enter vault name"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-primary-500 focus:border-primary-500"
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    handleCreateCategory();
                                                }
                                            }}
                                        />
                                        <div className="flex space-x-2">
                                            <button
                                                type="button"
                                                onClick={handleCreateCategory}
                                                disabled={creatingCategoryLoading || !newCategoryName.trim()}
                                                className="flex-1 py-2 px-3 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                            >
                                                {creatingCategoryLoading ? 'Creating...' : 'Create'}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setIsCreatingCategory(false);
                                                    setNewCategoryName('');
                                                }}
                                                className="flex-1 py-2 px-3 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 transition-colors"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>


                </div>

                {/* Middle Column - Account Credentials */}
                <div className="lg:col-span-1 space-y-6">
                    {/* Credentials Card */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <div className="flex items-center space-x-2 mb-6">
                            <div className="p-1 bg-green-100 rounded-lg">
                                <ShieldCheckIcon className="w-4 h-4 text-green-600" />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900">Account Credentials</h3>
                        </div>

                        <div className="space-y-6">
                            {/* Username Field */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Username/Email *
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <UserIcon className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <input
                                        type="text"
                                        name="username"
                                        value={formData.username}
                                        onChange={handleChange}
                                        onBlur={() => handleBlur('username')}
                                        className={`block w-full pl-10 pr-3 py-3 border rounded-xl text-sm placeholder-gray-400 transition-colors duration-200 ${touched.username && errors.username
                                                ? 'border-red-300 focus:ring-red-500 focus:border-red-500 bg-red-50'
                                                : 'border-gray-300 focus:ring-primary-500 focus:border-primary-500 hover:border-gray-400'
                                            }`}
                                        required
                                        placeholder="username or email@example.com"
                                    />
                                </div>
                                {touched.username && errors.username && (
                                    <p className="mt-2 text-sm text-red-600 flex items-center">
                                        <span className="font-medium">{errors.username}</span>
                                    </p>
                                )}
                            </div>

                            {/* Password Field */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Password *
                                </label>
                                <div className="space-y-3">
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <KeyIcon className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            name="password"
                                            value={formData.password}
                                            onChange={handleChange}
                                            onBlur={() => handleBlur('password')}
                                            className={`block w-full pl-10 pr-20 py-3 border rounded-xl text-sm placeholder-gray-400 transition-colors duration-200 ${touched.password && errors.password
                                                    ? 'border-red-300 focus:ring-red-500 focus:border-red-500 bg-red-50'
                                                    : 'border-gray-300 focus:ring-primary-500 focus:border-primary-500 hover:border-gray-400'
                                                }`}
                                            required
                                            placeholder="Enter your password"
                                        />
                                        <div className="absolute inset-y-0 right-0 flex items-center space-x-1 pr-3">
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                                            >
                                                {showPassword ? (
                                                    <EyeSlashIcon className="h-5 w-5" />
                                                ) : (
                                                    <EyeIcon className="h-5 w-5" />
                                                )}
                                            </button>
                                        </div>
                                    </div>

                                    {formData.password && (
                                        <PasswordStrengthMeter password={formData.password} />
                                    )}

                                    <button
                                        type="button"
                                        onClick={handleGeneratePassword}
                                        className="w-full flex items-center justify-center space-x-2 p-3 border border-dashed border-gray-300 rounded-xl text-primary-600 hover:text-primary-700 hover:border-primary-400 hover:bg-primary-50 transition-colors duration-200"
                                    >
                                        <SparklesIcon className="w-4 h-4" />
                                        <span className="text-sm font-medium">Generate Strong Password</span>
                                    </button>
                                </div>
                                {touched.password && errors.password && (
                                    <p className="mt-2 text-sm text-red-600 flex items-center">
                                        <span className="font-medium">{errors.password}</span>
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Column - Notes */}
                <div className="lg:col-span-1 space-y-6">
                    {/* Notes Card */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <div className="flex items-center space-x-2 mb-4">
                            <div className="p-1 bg-purple-100 rounded-lg">
                                <DocumentTextIcon className="w-4 h-4 text-purple-600" />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900">Additional Notes</h3>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Notes (Optional)
                            </label>
                            <textarea
                                name="notes"
                                rows={8}
                                value={formData.notes}
                                onChange={handleChange}
                                className="block w-full px-3 py-3 border border-gray-300 rounded-xl text-sm placeholder-gray-400 focus:ring-primary-500 focus:border-primary-500 hover:border-gray-400 transition-colors duration-200 resize-none"
                                placeholder="Add any additional notes or comments..."
                            />
                        </div>
                    </div>

                    {/* Preferences Card */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <div className="flex items-center space-x-2 mb-4">
                            <div className="p-1 bg-red-100 rounded-lg">
                                <HeartIcon className="w-4 h-4 text-red-600" />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900">Preferences</h3>
                        </div>

                        <div className="space-y-3">
                            <label className="flex items-center space-x-3 cursor-pointer p-3 rounded-lg hover:bg-gray-50 transition-colors duration-200">
                                <input
                                    type="checkbox"
                                    name="favourite"
                                    checked={formData.favourite}
                                    onChange={handleChange}
                                    className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                                />
                                <div className="flex items-center space-x-2">
                                    {formData.favourite ? (
                                        <HeartSolidIcon className="w-5 h-5 text-red-500" />
                                    ) : (
                                        <HeartIcon className="w-5 h-5 text-red-400" />
                                    )}
                                    <span className="text-sm font-medium text-gray-700">
                                        Mark as Favorite
                                    </span>
                                </div>
                            </label>

                            <label className="flex items-center space-x-3 cursor-pointer p-3 rounded-lg hover:bg-gray-50 transition-colors duration-200">
                                <input
                                    type="checkbox"
                                    name="auto_login"
                                    checked={autoLogin}
                                    onChange={(e) => setAutoLogin(e.target.checked)}
                                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                                />
                                <div className="flex items-center space-x-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-primary-600">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                                    </svg>
                                    <div>
                                        <span className="text-sm font-medium text-gray-700">
                                            Enable Auto-Login
                                        </span>
                                        <p className="text-xs text-gray-500">Automatically login when visiting this website</p>
                                    </div>
                                </div>
                            </label>
                        </div>
                    </div>
                </div>
            </div>

            {/* Enhanced Footer */}
            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-8 py-6 mt-8 -mx-8 -mb-8">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2 text-sm text-gray-500">
                        <ShieldCheckIcon className="w-5 h-5 text-green-500" />
                        <span>All data is encrypted and secure</span>
                    </div>
                    <div className="flex space-x-4">
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={onCancel}
                            className="px-6 py-3"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            loading={isLoading}
                            className="px-6 py-3 bg-primary-600 hover:bg-primary-700"
                        >
                            {isLoading ? (
                                <div className="flex items-center justify-center space-x-2">
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    <span>{initialData ? 'Updating...' : 'Saving...'}</span>
                                </div>
                            ) : (
                                <>
                                    <KeyIcon className="w-4 h-4 mr-2" />
                                    {initialData ? 'Update Password' : 'Save Password'}
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            </div>
        </form>
    );
};

export default PasswordForm;
